import primitiveFactory from "_src/engine/factories/primitive";
import objFactory from "_src/engine/factories/object";
import {primitiveType} from "_src/engine/constants";

import {vec4} from "_src/libs/gl-matrix";
import LineShader from "_src/shaders/line/line";
import selector from "_src/manipulators/libs/selector";
import workspace from "_src/workspace";
import config from "_src/config";
import objState from "_src/obj_state";

const BASE_LINE_COLOR = [0.8, 0.8, 0.8];
const BASE_LINE_WIDTH = 2;

const SELECTED_LINE_COLOR = [0.0, 1.0, 0.04];
const SELECTED_LINE_WIDTH = 2;

const PLANE_SELECTION_COLOR = [0.3, 0.6, 0.35];

export default class SectionWidget {
  constructor(sectionGroup) {
    this._sections = [];
    this.hash = sectionGroup.getHash();

    for (const sectionObject of sectionGroup.sections) {
        const sectionWidget = {
          transform: sectionObject.getProperty("transform"),
          contourModels: [],
        };

        for (const contourObject of sectionObject.contours) {
          const contourModel = createLine(contourObject.points);
          sectionWidget.contourModels.push(contourModel);
        }

        this._sections.push(sectionWidget);
    }
  }

  getModels() {
    const models = [];
    for (const section of this._sections) {
      models.push(...section.contourModels);
    }

    return models;
  }

  updateVisibility(sectionGroup) {
    for (let i = 0; i < sectionGroup.sections.length; i++) {
      const section = sectionGroup.sections[i];
      const sectionVisibility = sectionGroup.getVisibility() && section.getVisibility();

      for (let j = 0; j < section.contours.length; j++) {
        const contour = section.contours[j];
        const contourVisibility = sectionVisibility && contour.getVisibility();
        this._sections[i].contourModels[j].setVisibility(contourVisibility);
      }
    }
  }

  async updateSelection(sectionGroup, selectedObjectsSet) {
    const sectionGroupVisibility = sectionGroup.getVisibility();
    const sectionGroupSelection = sectionGroup.getSelection();
    for (let i = 0; i < sectionGroup.sections.length; i++) {
      const section = sectionGroup.sections[i];
      const object = workspace.getObjectByUUID(section.getProperty("objectUuid"));

      const sectionSelection = sectionGroupSelection || section.getSelection();
      const sectionVisibility = sectionGroupVisibility && section.getVisibility();
      if (sectionSelection && sectionVisibility && object && object.loaded && object.getVisibility()) {
        if (objState.checkIsUpdating(object)) {
          await objState.getUpdatePromise(object);
        }

        const m = section.getProperty("transform");
        const normal = vec4.transformMat4(vec4.create(), vec4.fromValues(0, 0, 1, 0), m);
        const center = vec4.transformMat4(vec4.create(), vec4.fromValues(0, 0, 0, 1), m);
        selector.selectByPlane(object, normal, center, false);
        selectedObjectsSet.add(object);
      }

      for (let j = 0; j < section.contours.length; j++) {
        const contour = section.contours[j];
        const contourSelection = sectionSelection || contour.getSelection();
        setContourSelected(this._sections[i].contourModels[j], contourSelection);
      }
    }
  }
}

function setContourSelected(contour, selected) {
  const color = selected ? SELECTED_LINE_COLOR : BASE_LINE_COLOR;
  const width = selected ? SELECTED_LINE_WIDTH : BASE_LINE_WIDTH;

  contour.setDiffuseColor(...color);
  contour.shader.setLineWidth(width);
}

function createLine(points) {
  const lineShader = new LineShader({
    lineWidth : BASE_LINE_WIDTH,
    transparent : true,
    backFaceCulling: false,
    depthTest: false,
    screenSpace : false
  });
  const lineModel = objFactory.createDrawable(lineShader);
  lineModel.setDiffuseColor(...BASE_LINE_COLOR);

  const primitives = [];
  for (let i = 1; i < points.length / 3; ++i) {
    primitives.push(i - 1, i);
  }

  lineModel.addGeometryChunk(primitiveFactory.create(primitiveType.LINE_ARRAY, { points, primitives }));
  lineModel.chunks[0].enableFrustumTest = false;

  return lineModel;
}

export async function prepareSectionSelection(sectionGroups) {
  const objectsSet = new Set();
  for (const sectionGroup of sectionGroups) {
    for (const section of sectionGroup.sections) {
      const object = workspace.getObjectByUUID(section.getProperty("objectUuid"));
      if (object && object.loaded) {
        objectsSet.add(object);
      }
    }
  }
  for (const object of objectsSet) {
    if (objState.checkIsUpdating(object)) {
      await objState.getUpdatePromise(object);
    }
    selector.dropSelection(object);
    object.frames[0].model.shader.setSelectionColor(config.selectionColor.r, config.selectionColor.g, config.selectionColor.b);
  }
}

export function finishSectionSelection(selectedObjectsSet) {
  for (const object of selectedObjectsSet) {
    for (const chunk of object.frames[0].model.chunks) {
      const geometry = chunk.geometry;

      if (!geometry.checkHasMarkedVertices()) {
        continue;
      }

      selector.applySelection(chunk, true);
    }

    object.frames[0].model.shader.setSelectionColor(...PLANE_SELECTION_COLOR);
  }
}
